import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPutawayPendingRequest, putawayRequest, restockRequest } from './operatorPutawayApi';
import {
  PUTAWAY_SOURCE,
  PUTAWAY_STEP,
  buildStoreBody,
  destinationProblem,
  matchPutawayRows,
  putawayRowOf,
  rowOption,
  suggestedDestination,
} from './operatorPutawayUtils';
import { putawayRowChosen } from './operatorPutawayActions';
import { scanCodeRequest } from '../operator/operatorApi';
import { OPERATOR_ERROR, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sameAction } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());


/** What is waiting to be stored from the tab on screen. */
export const fetchPutawayPending = createAsyncThunk(
  'operatorPutaway/fetchPending',
  async ({ page = 1 } = {}, { getState, fulfillWithValue, rejectWithValue }) => {
    try {
      const { source } = getState().operatorPutaway;
      const { data } = await getPutawayPendingRequest(warehouseOf(getState), { source, page });
      return fulfillWithValue(data, { receivedAt: Date.now() });
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar lo pendiente de guardar.'));
    }
  }
);

/**
 * The one pending row a task of the tray points to, which the screen opens
 * on. Answers the row, or `null` once it is no longer pending.
 *
 * @param {{source: 'receiving'|'restock', key: string}} arg
 */
export const fetchPutawayRow = createAsyncThunk(
  'operatorPutaway/fetchRow',
  async ({ source, key }, { getState, rejectWithValue }) => {
    try {
      const { data } = await getPutawayPendingRequest(warehouseOf(getState), { source, key });
      return (data.results || []).find((row) => row.key === key) || null;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos abrir lo que había que guardar.'));
    }
  }
);

/**
 * A code read while storing: the product of a pending row, then the slot it
 * goes to. A suggested slot is known already; any other is asked to the scan,
 * so a code that is not a slot, or a slot where stock cannot go, is said right
 * away instead of after the quantity.
 *
 * @param {{code: string, source: string}} read
 */
export const scanPutawayCode = createAsyncThunk(
  'operatorPutaway/scanCode',
  async ({ code }, { getState, rejectWithValue }) => {
    const putaway = getState().operatorPutaway;
    const value = cleanCode(code);

    if (putaway.step === PUTAWAY_STEP.PRODUCT) {
      const rows = matchPutawayRows(putaway.list.rows, value);
      if (rows.length === 1) return { kind: 'row', key: rows[0].key };
      if (rows.length > 1) return { kind: 'choose', options: rows.map(rowOption) };
      return rejectWithValue(localError(`No hay nada pendiente de guardar de ${value}.`));
    }

    const row = putawayRowOf(putaway);
    const suggested = suggestedDestination(row, value);
    if (suggested) return { kind: 'destination', destination: suggested };

    try {
      const { data } = await scanCodeRequest(warehouseOf(getState), value, ['slot']);
      const slot = (data.results || []).find((result) => result.kind === 'slot');
      if (!slot) return rejectWithValue(localError(`${value} no es una ubicación.`));
      const problem = destinationProblem(slot);
      if (problem) return rejectWithValue(localError(problem));
      return { kind: 'destination', destination: { code: slot.code, name: slot.name, suggested: false } };
    } catch (error) {
      const failure = readOperatorError(error, 'No pudimos leer esa ubicación.');
      return rejectWithValue(
        failure.kind === OPERATOR_ERROR.NOT_FOUND ? localError(`${value} no es una ubicación de esta bodega.`) : failure
      );
    }
  },
  {
    condition: (read, { getState }) => {
      const putaway = getState().operatorPutaway;
      const busy =
        Boolean(putaway.pending || putaway.resolution || putaway.scanning || putaway.requested?.loading) ||
        putaway.step === PUTAWAY_STEP.QUANTITY;
      return scanGate({ busy, lastCode: putaway.scan.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * Stores a row's goods in a slot, then reloads what is left.
 *
 * @param {{rowKey: string, source: string, sku: string, bucketId?: number, orderProductId?: number,
 *   lot?: string|null, toSlotCode: string, toSlotName?: string, quantity: number, rowQuantity?: number,
 *   extra?: object, idempotencyKey?: string}} arg - `rowQuantity`: what the row had, as the screen showed it.
 */
export const storePutaway = createAsyncThunk(
  'operatorPutaway/store',
  async (arg, { getState, dispatch, requestId, rejectWithValue }) => {
    try {
      const request = arg.source === PUTAWAY_SOURCE.RESTOCK ? restockRequest : putawayRequest;
      const { data } = await request(warehouseOf(getState), buildStoreBody(arg, requestId));
      await dispatch(fetchPutawayPending({ page: 1 }));
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos guardar.'));
    }
  },
  idempotentThunkOptions
);

/** Sends the store whose answer was lost again, under the same key. */
export const retryLastPutawayWrite = () => (dispatch, getState) => {
  const { lastWrite } = getState().operatorPutaway;
  return lastWrite ? dispatch(storePutaway(sameAction(lastWrite))) : null;
};

/**
 * The option the operator chose in the sheet: a pending row the code could
 * be, or what the server asked for (the bucket, the product or the lot), in
 * which case the same store goes again with it and the same key.
 *
 * @param {string|number} id
 */
export const choosePutawayOption = (id) => (dispatch, getState) => {
  const { resolution } = getState().operatorPutaway;
  if (!resolution || resolution.kind !== 'ambiguous') return null;
  if (resolution.origin === 'local') return dispatch(putawayRowChosen(id));
  const arg = { ...resolution.arg, extra: { ...resolution.arg.extra, [resolution.field]: id } };
  return dispatch(storePutaway(sameAction({ arg, key: resolution.key })));
};
